<template>
  <transition name="fade-overlay" appear>
    <div v-if="showOverlay" :class="$style.loadingOverlay">
      <HgSpinner
        :class="$style.spinner"
        :label="$t('valuationForm.propertyDetails.loading.text')"
      />
    </div>
  </transition>
  <FormHeader :formStep="formStep" @onBackClick="onBack" />
  <FormContainer>
    <TransitionWrapper>
      <FormTitle :class="$style.title">{{ $t('valuationForm.propertyDetails.title') }}</FormTitle>
      <FormContent>
        <Space direction="vertical">
          <div :class="$style.wrapper">
            <FormActions space="0">
              <div :class="$style.formField">
                <HgSelect
                  v-model="v$.rooms.$model"
                  name="rooms"
                  :label="$t('rooms.inputLabel')"
                  :options="roomSelectOptions"
                  :isDirty="v$.rooms.$dirty"
                  :isInvalid="v$.rooms.$invalid"
                >
                  <template #errorMessages>
                    <span v-if="v$.rooms.required.$invalid">
                      {{ $t('valuationForm.propertyDetails.numberOfRooms.required') }}
                    </span>
                  </template>
                </HgSelect>

                <HgInput
                  ref="livingSpace"
                  v-model="v$.livingSpace.$model"
                  name="livingSpace"
                  :label="$t('livingSpace.inputLabel')"
                  inputmode="numeric"
                  type="number"
                  pattern="[0-9]*"
                  :updateModelOnInput="true"
                  :isDirty="v$.livingSpace.$dirty"
                  :isInvalid="v$.livingSpace.$invalid"
                  unit="m²"
                  @wheel.prevent
                >
                  <template #errorMessages>
                    <span v-if="v$.livingSpace.required.$invalid">
                      {{ $t('valuationForm.propertyDetails.livingSpace.required') }}
                    </span>
                    <span v-if="v$.livingSpace.numeric.$invalid || v$.livingSpace.between.$invalid">
                      {{ $t('valuationForm.propertyDetails.livingSpace.numericInvalid') }}
                    </span>
                  </template>
                </HgInput>

                <HgInput
                  v-if="showLotSizeField"
                  ref="lotSize"
                  v-model="v$.lotSize.$model"
                  name="lotSize"
                  type="number"
                  :label="$t('lotSize.inputLabel')"
                  :updateModelOnInput="true"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  :isDirty="v$.lotSize.$dirty"
                  :isInvalid="v$.lotSize.$invalid"
                  unit="m²"
                  @wheel.prevent
                >
                  <template #errorMessages>
                    <span v-if="v$.lotSize.required.$invalid">
                      {{ $t('valuationForm.propertyDetails.lotSize.required') }}
                    </span>
                    <span v-if="!v$.lotSize.required.$invalid && v$.lotSize.greaterThan.$invalid">
                      {{ $t('valuationForm.propertyDetails.lotSize.numericInvalid') }}
                    </span>
                  </template>
                </HgInput>

                <HgInput
                  ref="buildYear"
                  v-model="v$.buildYear.$model"
                  name="buildYear"
                  :label="$t('yearBuilt.inputLabel')"
                  :maxlength="4"
                  type="number"
                  autocomplete="on"
                  :updateModelOnInput="true"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  :isDirty="v$.buildYear.$dirty"
                  :isInvalid="v$.buildYear.$invalid"
                  @wheel.prevent
                >
                  <template #errorMessages>
                    <span v-if="v$.buildYear.required.$invalid">
                      {{ $t('valuationForm.propertyDetails.yearBuilt.required') }}
                    </span>
                    <span v-if="v$.buildYear.min.$invalid">
                      {{ $t('valuationForm.propertyDetails.yearBuilt.numericInvalid') }}
                    </span>
                    <span v-if="v$.buildYear.max.$invalid">
                      {{ $t('yearBuilt.error.maxValue') }}
                    </span>
                  </template>
                </HgInput>
              </div>
            </FormActions>
          </div>
        </Space>
      </FormContent>
      <FormBottom :onNext="onNext" />
    </TransitionWrapper>
    <ToastMessage
      v-if="currentToastType != ToastType.PropertyTypeChangedApartment"
      :notificationWidth="BREAKPOINTS.funnel_container_max_width.toString() + 'px'"
    />
  </FormContainer>
  <ApartmentSelectionModal
    v-if="shouldOriginallyDisplayOverlay && !showOverlay"
    @onApartmentSelect="handleApartmentItemSelect"
  />
</template>

<script lang="ts" setup>
/* eslint-disable sonarjs/no-duplicate-string */
import {
  FormActions,
  FormBottom,
  FormContainer,
  FormContent,
  FormHeader,
  FormTitle,
  Space,
  TransitionWrapper,
} from '@/components/common';
import ToastMessage from '@/components/common/ToastMessage/ToastMessage.vue';
import {
  roomOptions,
  usePlaceholderOption,
} from '@/components/property-details-page/ValuationForm/util';
import ApartmentSelectionModal from '@/components/property-valuation-page/PropertyDetails/ApartmentSelectionModal.vue';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { inputStringToNumber } from '@/helpers/Number';
import { PropertyDetails } from '@/models/queries/getPropertyDetails';
import { PropertyTypeNumber } from '@/models/valuationFunnel';
import { usePropertyDetailsStore } from '@/stores/propertyDetails/propertyDetails';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { getStepInputs } from '@/tracking/tracking';
import {
  BREAKPOINTS,
  MAX_BUILD_YEAR,
  MAX_LIVING_SPACE,
  MIN_BUILD_YEAR,
  ToastType,
  ValuationFlow,
} from '@/types';
import { HgInput, HgSelect, HgSpinner, HgSupportedLanguage } from '@smg-real-estate/domus';
import { useVuelidate } from '@vuelidate/core';
import { between, maxValue, minValue, numeric, required, requiredIf } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const propertyDetailsStore = usePropertyDetailsStore();
const propertyValuationStore = usePropertyValuationStore();
const {
  data: formData,
  step: formStep,
  previousStep,
  propertyDetailsData,
} = storeToRefs(propertyValuationStore);
const { locale } = useI18n();
const route = useRoute();
const selectPlaceholderOption = usePlaceholderOption();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();

const DEFAULT_OVERLAY_DURATION = 1500;
const currentToastType = computed(() => propertyDetailsStore.toastType);
const showLotSizeField = computed(() => formData.value.propertyType === PropertyTypeNumber.House);
const originalPropertyDetailsDataExists: boolean = propertyDetailsData.value.length > 0;
const originalPropertyDetailsDataHasOneItem: boolean = propertyDetailsData.value.length === 1;

// Check if coming from address input steps (StreetAndNumber)
const isComingFromAddressStep: boolean = previousStep.value === ValuationFlow.StreetAndNumber;

const shouldOriginallyDisplayOverlay: boolean =
  isComingFromAddressStep && !originalPropertyDetailsDataHasOneItem;

const showOverlay = ref(false);
const loadingStartTime = ref(0);

const roomSelectOptions = computed(() => [selectPlaceholderOption.value].concat(roomOptions));

const model = reactive<{
  rooms: string;
  livingSpace: string | number;
  lotSize: string | number;
  buildYear: string | number;
}>({
  rooms: '',
  livingSpace: '',
  lotSize: '',
  buildYear: '',
});

const rules = computed(() => ({
  rooms: {
    required: (value: string) => {
      const numValue = inputStringToNumber(value);
      return !!numValue;
    },
  },
  livingSpace: {
    required,
    numeric,
    between: between(1, MAX_LIVING_SPACE),
  },
  lotSize: showLotSizeField.value
    ? {
        required: requiredIf(() => showLotSizeField.value === true),
        numeric,
        greaterThan: (value: string | number) => {
          const numValue = Number(value);
          return numValue > 0;
        },
      }
    : {},
  buildYear: {
    required,
    numeric,
    min: minValue(MIN_BUILD_YEAR),
    max: maxValue(MAX_BUILD_YEAR),
  },
}));

const v$ = useVuelidate(rules, model, { $autoDirty: true });

// To ensure formData is reactive after fetching the data in beforeMount
watch(
  () => formData.value,
  (newVal) => {
    model.rooms = (newVal.rooms || '').toString(); // Need to parse 0 to '' to displays the placeholder in HgSelect
    model.livingSpace = newVal.livingSpace ?? '';
    model.lotSize = newVal.lotSize ?? '';
    model.buildYear = newVal.buildYear ?? '';
  },
  { immediate: true, deep: true },
);

watch(
  () => model.rooms,
  (roomsValue) => {
    const parsedRooms = inputStringToNumber(roomsValue, true);
    if (parsedRooms && parsedRooms !== formData.value.rooms) {
      propertyValuationStore.setFormData({ rooms: parsedRooms });
    }
  },
);

watch(
  () => model.livingSpace,
  (livingSpaceValue) => {
    const parsedLivingSpace = inputStringToNumber(String(livingSpaceValue));
    if (
      parsedLivingSpace &&
      parsedLivingSpace >= 1 &&
      parsedLivingSpace <= MAX_LIVING_SPACE &&
      parsedLivingSpace !== formData.value.livingSpace
    ) {
      propertyValuationStore.setFormData({ livingSpace: parsedLivingSpace });
    }
  },
);

watch(
  () => model.buildYear,
  (buildYearValue) => {
    const parsedBuildYear = inputStringToNumber(String(buildYearValue));
    if (
      parsedBuildYear &&
      parsedBuildYear >= MIN_BUILD_YEAR &&
      parsedBuildYear <= MAX_BUILD_YEAR &&
      parsedBuildYear !== formData.value.buildYear
    ) {
      propertyValuationStore.setFormData({ buildYear: parsedBuildYear });
    }
  },
);

watch(
  () => model.lotSize,
  (lotSizeValue) => {
    if (!showLotSizeField.value) {
      return;
    }

    const parsedLotSize = inputStringToNumber(String(lotSizeValue));
    if (parsedLotSize && parsedLotSize > 0 && parsedLotSize !== formData.value.lotSize) {
      propertyValuationStore.setFormData({ lotSize: parsedLotSize });
    }
  },
);

onBeforeMount(async () => {
  v$.value.$reset();
  // Displays the overlay with the following conditions:
  // 1. The user comes from the Address step
  // 2. The property details data does not exist or exists but with more than one item
  if (shouldOriginallyDisplayOverlay) {
    loadingStartTime.value = Date.now();
    showOverlay.value = true;

    if (!originalPropertyDetailsDataExists) {
      await propertyValuationStore.fetchPropertyDetailsData(locale.value as HgSupportedLanguage);
    }

    // Displays the overlay for a minimum of 1.5 seconds
    const loadingTime = Date.now() - loadingStartTime.value;
    const remainingTime = Math.max(0, DEFAULT_OVERLAY_DURATION - loadingTime);
    setTimeout(async () => {
      showOverlay.value = false;
      v$.value.$reset();

      //Check for change in property Type
      if (propertyDetailsData.value.length > 0) {
        checkPropertyType();
      }

      // Display toast notification if the selected item is prefilled directly
      if (propertyDetailsData.value.length === 1) {
        const selectedItem = propertyDetailsData.value[0];
        if (isNotificationVisible(selectedItem)) {
          propertyDetailsStore.setToastType(ToastType.PrefilledData);
        }
      }
    }, remainingTime);
  }
});

const checkPropertyType = () => {
  const fetchedPropertyType =
    propertyDetailsData.value.length === 1
      ? PropertyTypeNumber.House
      : PropertyTypeNumber.Apartment;
  if (formData.value.propertyType != fetchedPropertyType) {
    propertyValuationStore.setFormData({ propertyType: fetchedPropertyType });
    if (fetchedPropertyType === PropertyTypeNumber.Apartment) {
      propertyDetailsStore.setToastType(ToastType.PropertyTypeChangedApartment);
    }
  }
};

onMounted(() => {
  trackVirtualPageViewWithTreatment({
    subType: 'input_property_details',
    locale: locale.value,
    stepInputs: getStepInputs(formData.value, (route?.query?.source as any) || '', formStep.value),
  });
});

onBeforeUnmount(() => {
  propertyDetailsStore.setToastType(ToastType.None);
});

const isNotificationVisible = (propertyData: PropertyDetails): boolean =>
  formData.value.propertyType === PropertyTypeNumber.Apartment
    ? Object.keys(propertyData).some((key) => key !== 'lotSize')
    : Object.keys(propertyData).length > 0;

const handleApartmentItemSelect = async (selectedItem: PropertyDetails) => {
  propertyValuationStore.setFormData({
    rooms: inputStringToNumber(selectedItem.numberOfRooms?.toString() ?? '', true),
    livingSpace: inputStringToNumber(selectedItem.livingSpace?.toString() ?? ''),
    buildYear: inputStringToNumber(selectedItem.buildYear?.toString() ?? ''),
    ...(showLotSizeField.value
      ? { lotSize: inputStringToNumber(selectedItem.lotSize?.toString() ?? '') }
      : {}),
  });
  await nextTick();
  v$.value.$reset();

  if (isNotificationVisible(selectedItem)) {
    propertyDetailsStore.setToastType(ToastType.PrefilledData);
  }
};

const onBack = () => {
  propertyValuationStore.navigateBack(ValuationFlow.PropertyDetails);
};

const onNext = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) {
    return;
  }
  const buildYear = inputStringToNumber(String(v$.value.buildYear.$model));
  propertyValuationStore.setFormData({
    rooms: inputStringToNumber(v$.value.rooms.$model, true),
    livingSpace: inputStringToNumber(String(v$.value.livingSpace.$model)),
    buildYear,
    ...(showLotSizeField.value
      ? { lotSize: inputStringToNumber(String(v$.value.lotSize.$model)) }
      : {}),
  });

  propertyValuationStore.navigateNext(ValuationFlow.PropertyDetails);
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.loadingOverlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(10, 10, 10, 0.5);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  > div {
    color: $color-white;
  }
  h3 {
    @include body-3($color-white);
    & {
      line-height: 18px;
    }
  }
}

:global(.fade-overlay-enter-active),
:global(.fade-overlay-leave-active),
:global(.fade-overlay-appear-active) {
  transition: opacity 200ms ease-in 50ms;
}

:global(.fade-overlay-enter-from),
:global(.fade-overlay-leave-to),
:global(.fade-overlay-appear-from) {
  opacity: 0;
}

.title {
  &:focus {
    outline: none;
  }
}

.wrapper {
  position: relative;
  margin-bottom: $space-200;

  .formField {
    display: flex;
    flex-direction: column;
    margin-bottom: $space-150;
    gap: $space-100;
    width: 100%;

    &:last-child {
      margin-bottom: 0;
    }
  }

  [class*='errorMessages'] {
    span {
      display: block;
      font-size: $space-75;
      line-height: 1.4;
    }
  }
}
</style>
